import React from 'react';
import { FiCpu, FiSearch, FiZap, FiDollarSign, FiUsers, FiStar, FiFlag, FiCheckCircle } from 'react-icons/fi';
import GlassCard from '../ui/GlassCard';

/**
 * AIDocumentation - A component for displaying documentation about AI features
 * 
 * @param {Object} props - Component props
 * @param {string} props.feature - The AI feature to show documentation for ('search', 'screening', or 'all')
 * @param {string} props.planType - The user's subscription plan type
 */
const AIDocumentation = ({ feature = 'all', planType = 'basic' }) => {
  // Determine which model is used based on plan type
  const getModelForPlan = (plan) => {
    const planLower = plan.toLowerCase();
    if (planLower === 'pro' || planLower === 'elite') {
      return 'GPT-4o';
    }
    return 'GPT-3.5 Turbo';
  };
  
  const model = getModelForPlan(planType);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
          <FiCpu size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">AI Features Documentation</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Using {model} for {planType} plan
          </p>
        </div>
      </div>
      
      {/* AI Search Documentation */}
      {(feature === 'all' || feature === 'search') && (
        <GlassCard variant="light" className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiSearch className="text-indigo-500" size={18} />
            <h3 className="text-lg font-medium text-gray-800 dark:text-white">AI Candidate Search</h3>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              AI Candidate Search uses advanced natural language processing to find the best candidates in your database based on job requirements or custom search criteria.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How It Works</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>Select a job posting or enter custom search criteria</li>
                <li>AI analyzes the requirements and searches through your candidate database</li>
                <li>Candidates are scored based on skills, experience, education, and other factors</li>
                <li>Results are ranked by match score, with only candidates scoring 80% or higher shown</li>
                <li>Each candidate includes a match explanation and detailed score breakdown</li>
              </ol>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <FiDollarSign className="text-indigo-500" size={16} />
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                1 AI credit is used per candidate profile processed
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FiZap className="text-indigo-500" size={16} />
                  <h4 className="font-medium text-gray-800 dark:text-white">Key Features</h4>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <li>Semantic understanding of job requirements</li>
                  <li>Contextual skill matching</li>
                  <li>Experience level evaluation</li>
                  <li>Education relevance scoring</li>
                  <li>Location preference matching</li>
                </ul>
              </div>
              
              <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FiStar className="text-indigo-500" size={16} />
                  <h4 className="font-medium text-gray-800 dark:text-white">Best Practices</h4>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <li>Be specific in custom search criteria</li>
                  <li>Include must-have skills and experience</li>
                  <li>Specify education requirements if relevant</li>
                  <li>Adjust the number of profiles to search based on your needs</li>
                  <li>Review match explanations to understand AI decisions</li>
                </ul>
              </div>
            </div>
          </div>
        </GlassCard>
      )}
      
      {/* AI Screening Documentation */}
      {(feature === 'all' || feature === 'screening') && (
        <GlassCard variant="light" className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiUsers className="text-indigo-500" size={18} />
            <h3 className="text-lg font-medium text-gray-800 dark:text-white">AI Applicant Screening</h3>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              AI Applicant Screening automatically evaluates job applicants against the job requirements, saving you time and helping you identify the best candidates quickly.
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800 dark:text-white mb-2">How It Works</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li>Select a job with applicants to screen</li>
                <li>AI analyzes each applicant's resume against the job requirements</li>
                <li>Each applicant receives a suitability score from 0-100</li>
                <li>AI identifies potential flags or edge cases (e.g., missing key requirements, overqualified)</li>
                <li>Results are ranked by score with detailed explanations</li>
              </ol>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <FiDollarSign className="text-indigo-500" size={16} />
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                1 AI credit is used per applicant screened
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FiCheckCircle className="text-indigo-500" size={16} />
                  <h4 className="font-medium text-gray-800 dark:text-white">Screening Criteria</h4>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <li>Skills match with job requirements</li>
                  <li>Experience level and relevance</li>
                  <li>Education qualifications</li>
                  <li>Technical proficiency</li>
                  <li>Career trajectory alignment</li>
                </ul>
              </div>
              
              <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FiFlag className="text-indigo-500" size={16} />
                  <h4 className="font-medium text-gray-800 dark:text-white">Flag Types</h4>
                </div>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <li>Missing key requirements</li>
                  <li>Overqualified candidates</li>
                  <li>Career gaps or inconsistencies</li>
                  <li>Potential skill mismatches</li>
                  <li>Location or availability issues</li>
                </ul>
              </div>
            </div>
          </div>
        </GlassCard>
      )}
      
      {/* Model Information */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 dark:text-white mb-2">AI Model Information</h4>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
          <p>
            <span className="font-medium">Current Model:</span> {model}
          </p>
          <p>
            <span className="font-medium">Capabilities:</span> {' '}
            {model === 'GPT-4o' 
              ? 'Advanced semantic understanding, nuanced context awareness, superior reasoning, and detailed analysis.'
              : 'Good semantic understanding, basic context awareness, and effective pattern recognition.'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Upgrade to a Pro or Elite plan to access GPT-4o for more accurate and nuanced AI results.
          </p>
        </div>
      </div>
    </div>

  );
};

export default AIDocumentation;
